import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import back from '../../../static/img/back.svg';
import './styles.css';
import tickBlanco from '../../../static/img/tickBlanco.svg';
import Footer from '../footer/footer';
import {
  actualizarBloque,
  getDetalleEjercicio,
} from '../../../services/deportistaApi';
import { getUserId } from '../../../services/utilsApi';
import LoadingScreen from '../../../static/assets/loadingScreen/loadingScreen';

const DetalleBloque = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { bloqueCompleto, bloque_numero, selectedDay } = location.state;

  const [bloque, setBloque] = useState(bloqueCompleto);
  const [videosUrl, setVideosUrl] = useState({});
  const [datosModificados, setDatosModificados] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleBack = (dia) => {
    navigate(`/entrenamiento`, { state: dia });
  };

  const goToMovement = (data) => {
    navigate(`/entrenamiento/bloque/movimiento`, {
      state: { ...data, selectedDay },
    });
  };

  const limpiarNumeroParaInput = (valor, permitirComa) => {
    let texto = String(valor ?? '');

    if (permitirComa) {
      texto = texto.replace(/\./g, ',');
    }

    texto = texto.replace(permitirComa ? /[^\d,]/g : /[^\d]/g, '');

    if (permitirComa) {
      const partes = texto.split(',');
      if (partes.length > 2) {
        texto = partes[0] + ',' + partes.slice(1).join('');
      }
    }

    return texto;
  };

  const normalizarEnBlur = (valor, permitirComa) => {
    let texto = limpiarNumeroParaInput(valor, permitirComa);

    if (permitirComa && texto.endsWith(',')) {
      texto = texto.slice(0, -1);
    }

    if (permitirComa && texto.startsWith(',')) {
      texto = '0' + texto;
    }

    return texto;
  };

  const fetchVideoUrl = async (id_ejercicio) => {
    try {
      const health = await getUserId();
      const uid = health.data.user_id;
      const response = await getDetalleEjercicio(uid, id_ejercicio);
      if (response.data) {
        return response.data.ejercicio.video;
      }
    } catch (error) {
      console.error('Error fetching video URL:', error);
    }
    return null;
  };

  useEffect(() => {
    const fetchVideos = async () => {
      const newVideosUrl = {};
      const promises = bloqueCompleto.ejercicios.map(async (ejercicio) => {
        if (ejercicio?.id_ejercicio) {
          const url = await fetchVideoUrl(ejercicio.id_ejercicio);
          newVideosUrl[ejercicio.id_ejercicio] = url;
        }
      });

      await Promise.all(promises);
      setVideosUrl(newVideosUrl);
      setLoading(false);
    };

    fetchVideos();
  }, [bloqueCompleto.ejercicios]);

  const handleInputChange = (ejercicioIndex, serieIndex, key, event) => {
    const permitirComa = key === 'peso';
    const valorTexto = limpiarNumeroParaInput(event.target.value, permitirComa);

    const newBloque = { ...bloque, ejercicios: [...bloque.ejercicios] };
    newBloque.ejercicios[ejercicioIndex].series[serieIndex][key] = valorTexto;

    setDatosModificados(true);
    setBloque(newBloque);
  };

  const handleInputBlur = (ejercicioIndex, serieIndex, key) => {
    const permitirComa = key === 'peso';

    const newBloque = { ...bloque, ejercicios: [...bloque.ejercicios] };
    const serie = newBloque.ejercicios[ejercicioIndex].series[serieIndex];

    const valorActual = String(serie[key] ?? '');
    const valorNormalizado = normalizarEnBlur(valorActual, permitirComa);
    if (valorNormalizado !== valorActual) {
      serie[key] = valorNormalizado;
      setDatosModificados(true);
      setBloque(newBloque);
    }
  };

  const toggleCompletion = (ejercicioIndex, serieIndex) => {
    const newBloque = { ...bloque, ejercicios: [...bloque.ejercicios] };
    const serie = newBloque.ejercicios[ejercicioIndex].series[serieIndex];
    serie.completado = !serie.completado;

    setDatosModificados(true);
    setBloque(newBloque);
  };

  const enviarDatos = useCallback(() => {
    if (!bloque || !bloque.ejercicios) return;

    if (datosModificados) {
      const datosParaEnviar = {
        ejercicios: bloque.ejercicios.map((ejercicio) => ({
          id_ejercicio_data: ejercicio.id_ejercicio_data,
          series: ejercicio.series.map((serie) => ({
            id_serie: serie.id_serie,
            repeticiones:
              parseInt(
                String(serie.repeticiones ?? '').replace(/[^\d]/g, ''),
                10
              ) || 0,
            peso: parseFloat(String(serie.peso ?? '').replace(',', '.')) || 0,
            completado: serie.completado,
          })),
        })),
      };

      actualizarBloque(datosParaEnviar)
        .then(() => setDatosModificados(false))
        .catch((error) => {
          console.error('Error actualizando bloque:', error);
        });
    }
  }, [bloque, datosModificados]);

  useEffect(() => {
    const handler = () => enviarDatos();
    window.addEventListener('beforeunload', handler);

    return () => {
      window.removeEventListener('beforeunload', handler);
      enviarDatos();
    };
  }, [enviarDatos]);

  const areAllExercisesCompleted = () => {
    return bloque.ejercicios.every((ejercicio) =>
      ejercicio.series.every((serie) => serie.completado)
    );
  };

  const completadoTotal = () => {
    setDatosModificados(true);
    enviarDatos();
    handleBack(selectedDay);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="detalle-ejercicio-page">
      <div className="detalle-bloque">
        <div className="detalle-ejercicio-header">
          <button
            className="bloque-back-button"
            onClick={() => handleBack(selectedDay)}
          >
            <img src={back} alt="Regresar" />
          </button>
          <div className="titulo">
            <h1 className="bloque-tittle">{bloque_numero}</h1>
          </div>
        </div>

        <h2 className="bloque-nombre">{bloque.titulo}</h2>
        <h3 className="bloque-descripcion">{bloque.subtitulo}</h3>

        {bloque.ejercicios.map((ejercicio, ejercicioIndex) => (
          <div key={ejercicioIndex}>
            <h3 className="ejercicio-nombre">{ejercicio.nombre}</h3>

            {ejercicio.notas_entrenador && (
              <h3 className="ejercicio-nota-entrenador">
                {ejercicio.notas_entrenador}
              </h3>
            )}

            <div className="ejercicio-detalles">
              {ejercicio.series.length !== 0 && (
                <div className="ejercicio-headers">
                  <span>Series</span>
                  <span>Reps.</span>
                  <span>%</span>
                  <span>Kg</span>
                  <span></span>
                </div>
              )}

              {ejercicio.series.map((serie, serieIndex) => (
                <div key={serieIndex} className="ejercicio-series">
                  <span>{serie.serie}</span>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={serie.repeticiones}
                    onChange={(e) =>
                      handleInputChange(
                        ejercicioIndex,
                        serieIndex,
                        'repeticiones',
                        e
                      )
                    }
                    onBlur={() =>
                      handleInputBlur(
                        ejercicioIndex,
                        serieIndex,
                        'repeticiones'
                      )
                    }
                  />

                  <span>{serie.porciento}</span>

                  <input
                    type="text"
                    inputMode="decimal"
                    value={serie.peso}
                    onChange={(e) =>
                      handleInputChange(ejercicioIndex, serieIndex, 'peso', e)
                    }
                    onBlur={() =>
                      handleInputBlur(ejercicioIndex, serieIndex, 'peso')
                    }
                  />

                  <div
                    className={`entrenamiento-ellipse ${
                      serie.completado ? 'complete' : ''
                    }`}
                    data-testid="completado-toggle"
                    onClick={() => toggleCompletion(ejercicioIndex, serieIndex)}
                  >
                    {serie.completado ? (
                      <img
                        src={tickBlanco}
                        alt="Completed"
                        className="entrenamiento-tick-icon"
                      />
                    ) : (
                      <span className="entrenamiento-ellipse-text">
                        {serie.subgrupo}
                        {serie.subgrupo_orden}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {videosUrl[ejercicio.id_ejercicio] && (
              <h3
                className="ejercicio-video"
                onClick={() =>
                  goToMovement({
                    id_ejercicio: ejercicio.id_ejercicio,
                    bloque_numero,
                    bloqueCompleto: bloque,
                  })
                }
              >
                Ver ejercicio
              </h3>
            )}
          </div>
        ))}

        <div
          className={
            areAllExercisesCompleted()
              ? 'completed-div-complete'
              : 'completed-div-incomplete'
          }
          onClick={completadoTotal}
        >
          <span
            className={
              areAllExercisesCompleted()
                ? 'completed-text-complete'
                : 'completed-text-incomplete'
            }
          >
            Completado
          </span>
        </div>
      </div>

      <Footer activeScreen="entrenamiento" />
    </div>
  );
};

export default DetalleBloque;
